import React, { useMemo, useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Chip, SelectItem, SharedSelection, TmdxSelect } from '@ds';
import { AdminFormRow } from '@ui';
import { noop } from 'lodash-es';
import { useInfiniteTXCentersListQuery } from '@/queries/tx-centers';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';
import { CustomerInTxCenterForm, UpdateTXUserFormValues } from '@/types/customer';
import { txCenterMapper } from '../../utils';
import { getCombinedTxCenterList, getUpdatedCustomerInTxCenters, sortSelectedItems } from '../utils';

type Props = {
  isReadOnly?: boolean;
  replace: (items: CustomerInTxCenterForm[]) => void;
};

export const TXCenterMultiSelect = React.memo(({ isReadOnly = false, replace }: Props) => {
  const { control, trigger } = useFormContext<UpdateTXUserFormValues>();

  const customerInTxCenters = useWatch({
    control,
    name: 'customerInTxCenters',
  });

  const {
    searchValue,
    list: infiniteDropdownTxCentersList,
    onSearchValue,
    itemsScrollerRef,
    isLoading,
    setAutocompleteOpen: setSelectOpen,
  } = useInfiniteDropdown({
    setValue: noop,
    listQuery: useInfiniteTXCentersListQuery,
    clearOnSearch: false,
    mapper: txCenterMapper,
    defaultSortOrder: { orderBy: 'name', asc: true },
  });

  const list = useMemo(
    () => getCombinedTxCenterList(customerInTxCenters, infiniteDropdownTxCentersList),
    [infiniteDropdownTxCentersList, customerInTxCenters],
  );

  const onSelectionChange = useCallback(
    (keys: SharedSelection) => {
      const selectedIDs = Array.from(keys) as string[];
      const currentCenters = customerInTxCenters ?? [];
      const selectedItems = getUpdatedCustomerInTxCenters(selectedIDs, currentCenters, list);

      replace(selectedItems);
      trigger('email');
    },
    [customerInTxCenters, list, replace, trigger],
  );

  return (
    <AdminFormRow title="TX Center(s)" isRequired inputWrapperClass="max-w-160">
      <Controller
        name="customerInTxCenters"
        control={control}
        render={({ field: { value, ref, onChange, ...field }, fieldState: { invalid, error } }) => (
          <TmdxSelect
            items={list}
            variant="bordered"
            isMultiline
            selectionMode="multiple"
            placeholder="Select TX Centers"
            searchValue={searchValue}
            onSearchValue={onSearchValue}
            aria-label="TX Centers"
            isInvalid={invalid}
            isDisabled={isReadOnly}
            errorMessage={error?.message}
            selectedKeys={(value as CustomerInTxCenterForm[] | undefined)?.map((center) => center.txCenterId) || []}
            onSelectionChange={onSelectionChange}
            scrollRef={itemsScrollerRef}
            isLoading={isLoading}
            onOpenChange={setSelectOpen}
            {...field}
            renderValue={(items) => {
              const selectedItems = sortSelectedItems(items);
              return (
                <div className="flex gap-2 flex-wrap">
                  {selectedItems.map((item) => (
                    <Chip key={item.value}>{item.name}</Chip>
                  ))}
                </div>
              );
            }}
          >
            {(selectItem) => {
              return (
                <SelectItem textValue={selectItem.name} key={selectItem.value}>
                  <span className="text-small">{selectItem.name}</span>
                </SelectItem>
              );
            }}
          </TmdxSelect>
        )}
      />
    </AdminFormRow>
  );
});

TXCenterMultiSelect.displayName = 'TXCenterMultiSelect';
